import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AppAuthService } from '@services/core/auth/app-auth.service';
import { SupportCasesApiService } from './support-cases-api.service';

@Injectable({
  providedIn: 'root',
})
export class SupportCasesBacklogBadgeService {
  readonly count = signal(0);
  readonly loading = signal(false);

  private refreshPromise: Promise<void> | null = null;
  private refreshQueued = false;

  private readonly api: SupportCasesApiService;
  private readonly authService: AppAuthService;

  constructor(
    api?: SupportCasesApiService,
    authService?: AppAuthService,
  ) {
    this.api = api ?? inject(SupportCasesApiService);
    this.authService = authService ?? inject(AppAuthService);
  }

  async refresh(): Promise<void> {
    if (!this.authService.hasRole('developer')) {
      this.count.set(0);
      return;
    }

    this.refreshQueued = true;

    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = this.drainRefreshQueue();
    return this.refreshPromise;
  }

  increment(by = 1): void {
    this.count.update(curr => Math.max(0, curr + by));
  }

  decrement(by = 1): void {
    this.count.update(curr => Math.max(0, curr - by));
  }

  setCount(count: number): void {
    this.count.set(Number.isFinite(count) ? Math.max(0, count) : 0);
  }

  private async drainRefreshQueue(): Promise<void> {
    if (!this.authService.hasRole('developer')) {
      this.count.set(0);
      return;
    }

    this.loading.set(true);

    try {
      while (this.refreshQueued) {
        this.refreshQueued = false;

        if (!this.authService.hasRole('developer')) {
          this.count.set(0);
          break;
        }

        try {
          const count = await firstValueFrom(this.api.getBacklogCount());
          this.count.set(Number.isFinite(count) ? Math.max(0, count) : 0);
        } catch {
          // Keep the previous count if the badge refresh fails.
        }
      }
    } finally {
      this.loading.set(false);
      this.refreshPromise = null;
    }
  }
}
